import { useQuery } from '@tanstack/react-query';

import { request } from '~/services/common';

import { canAccessLeLens } from '~/utils/canAccessLeLens';
import isProd from '~/utils/isProd';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

import useCurrentUser from './useCurrentUser';

const DENIED_ACCESS_STALE_MS = 30_000;

export function useLensAccessDecision() {
  const { user } = useCurrentUser();
  const needsLookup = Boolean(isProd() && user?.email && !user.roles?.includes('reporting-admin'));
  const access = useQuery({
    queryKey: ['askRichard', 'pilot-access', user?.email],
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeDataUrl('/api/reporting/le-data/pilot-access'), {
        signal,
      })) as { allowed: boolean };
      if (typeof response?.allowed !== 'boolean') {
        throw new Error('Invalid askRichard access response');
      }
      return response.allowed;
    },
    enabled: needsLookup,
    // Keep an allowed workspace stable, but let a newly granted user be checked again on navigation.
    staleTime: (query) => (query.state.data === true ? Infinity : DENIED_ACCESS_STALE_MS),
    gcTime: Infinity,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    retryOnMount: true,
  });

  return {
    canAccess: canAccessLeLens(user, access.data === true),
    isLoading: needsLookup && Boolean(access.isPending || (access.data !== true && access.isFetching)),
    // A failed refetch retains a confirmed allow, but must not be mistaken for a denial.
    isError: needsLookup && access.isError && access.data !== true,
    retryAccess: access.refetch,
  };
}

export default function useCanAccessLeLens(): boolean {
  return useLensAccessDecision().canAccess;
}
